import React, { useEffect, useState } from 'react'
import pick from 'lodash/pick'

import { useToast } from '@core/toast'
import { localizedStrings } from '@core/strings'
import {
  useCollectBriefingSignatures,
  useCreateBriefingSession,
  useUpdateBriefingSession,
} from '@core/react-query/features/briefing'
import ConfirmModal from '@common/modal/ConfirmModal'
import {
  transformParticipantsToPayloadFormat,
  briefingsGenericHooksByEntityType,
  ParticipantEntityType,
} from '@core/modules/briefings'

import { useModal } from '@common/GlobalModal'
import { ModalDialogContainer } from '@common/modal'
import { Clear as ClearIcon } from '@mui/icons-material'
import {
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  LoadingButton,
} from '@common/material'
import {
  BriefingParticipantsPicker,
  ParticipantLocal,
} from './BriefingParticipantsPicker'

type BriefingAddParticipantsModalProps = {
  entityId: number
  entityType: ParticipantEntityType
}

export const BriefingAddParticipantsModal: React.FC<
  BriefingAddParticipantsModalProps
> = ({ entityId, entityType }) => {
  const toast = useToast()
  const { showModal, hideModal } = useModal()

  const [startedOn] = useState(new Date().toISOString())
  const [selectedParticipants, setSelectedParticipants] = useState<
    ParticipantLocal[]
  >([])

  const { useGetEntityById, useGetEntityRoles } =
    briefingsGenericHooksByEntityType[entityType]
  const { data: entity } = useGetEntityById({ id: entityId })
  const { data: entityRoles } = useGetEntityRoles({ id: entityId })

  const isOngoingBriefingSession = entity?.hasOngoingBriefingSession() ?? false
  const requiresBriefingSignature = entity?.requiresBriefingSignature()

  const {
    mutate: createBriefingSession,
    isLoading: isCreatingBriefingSession,
  } = useCreateBriefingSession()
  const {
    mutate: updateBriefingSession,
    isLoading: isUpdatingBriefingSession,
  } = useUpdateBriefingSession()
  const {
    mutate: collectBriefingSignatures,
    isLoading: isCollectingSignatures,
  } = useCollectBriefingSignatures()

  const isLoading =
    isCreatingBriefingSession ||
    isUpdatingBriefingSession ||
    isCollectingSignatures

  useEffect(() => {
    // Seed selection from the ongoing briefing session if one exists.
    if (isOngoingBriefingSession) {
      const unsignedParticipants =
        entity.lastBriefingSession.getUnsignedParticipants()

      setSelectedParticipants(
        unsignedParticipants.map((participant) => ({
          ...pick<typeof participant, keyof typeof participant>(participant, [
            'userId',
            'participantId',
            'sessionParticipantId',
            'firstName',
            'lastName',
            'company',
          ]),
          roleId: participant.briefOnRoleId,
        })),
      )
    }
  }, [!!entity])

  const handleConfirm = () => {
    const transformedParticipants =
      transformParticipantsToPayloadFormat(selectedParticipants)

    const briefingPeriod = {
      startedOn,
      endedOn: new Date().toISOString(),
    }

    const options = {
      onSuccess: () => {
        hideModal()
      },
      onError: (error) => {
        toast.error(error.message ?? localizedStrings.genericError)
      },
    }

    if (!requiresBriefingSignature) {
      const justBriefedParticipants = transformedParticipants.map(
        (participant) => ({ ...participant, justBriefed: true }),
      )

      const briefedParticipants = isOngoingBriefingSession
        ? [
            ...entity.lastBriefingSession
              .getSignedParticipants()
              .filter(
                (p1) =>
                  !justBriefedParticipants.some(
                    (p2) => p2.participantId === p1.participantId,
                  ),
              ),
            ...justBriefedParticipants,
          ]
        : justBriefedParticipants

      showModal(
        <ConfirmModal
          title={localizedStrings.addParticipants}
          onConfirm={() => {
            hideModal()
            collectBriefingSignatures(
              {
                organisationId: entity.organisationId,
                briefingSessionId: isOngoingBriefingSession
                  ? entity.lastBriefingSession.id
                  : undefined,
                referenceObjectId: entityId,
                referenceObjectType: entityType,
                participants: briefedParticipants,
                briefingPeriod,
              },
              options,
            )
          }}
        >
          {localizedStrings.youAreAboutToAddParticipantsToPermit(
            selectedParticipants.length,
          )}
        </ConfirmModal>,
      )
      return
    }

    if (isOngoingBriefingSession) {
      const payloadParticipants = [
        ...entity.lastBriefingSession
          .getSignedParticipants()
          .filter(
            (p1) =>
              !transformedParticipants.some(
                (p2) => p2.participantId === p1.participantId,
              ),
          ),
        ...transformedParticipants,
      ]

      updateBriefingSession(
        {
          briefingSessionId: entity.lastBriefingSession.id,
          referenceObjectId: entityId,
          referenceObjectType: entityType,
          participants: payloadParticipants,
          briefingPeriod,
        },
        options,
      )
    } else {
      createBriefingSession(
        {
          organisationId: entity.organisationId,
          referenceObjectId: entityId,
          referenceObjectType: entityType,
          participants: transformedParticipants,
          briefingPeriod,
        },
        options,
      )
    }
  }

  return (
    <ModalDialogContainer maxWidth='md' PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20, mb: 0 }}
      >
        {localizedStrings.addParticipants}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', p: 0 }}>
        <BriefingParticipantsPicker
          participants={selectedParticipants}
          onChange={setSelectedParticipants}
          roles={entityRoles}
          projectId={entity?.project?.id}
          organisationId={entity?.organisationId}
          entity={entity}
          entityType={entityType}
          showAddExternalUser={!!requiresBriefingSignature}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ pt: 2, pb: 2, pl: 3, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          variant='contained'
          color='primary'
          onClick={handleConfirm}
          disabled={!selectedParticipants.length}
          loading={isLoading}
        >
          {localizedStrings.confirm}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
